import { useQueryClient } from "@tanstack/react-query";
import { ImagePlus } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";

import { useApi } from "../../app/api-context";
import { describeError } from "../../lib/errors";
import { CARDS_KEY } from "./card-queries";

interface CardPictureProps {
  readonly cardId: string;
  readonly name: string;
  /** null: no picture yet. */
  readonly version: string | null;
  readonly canChange: boolean;
}

/** The card's picture (D-67); click to add or replace it (JPEG, PNG or WebP, at most 5 MB). */
export function CardPicture({ cardId, name, version, canChange }: CardPictureProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const inputId = useId();

  async function upload(file: File | undefined) {
    if (file === undefined) return;
    setBusy(true);
    try {
      const response = await api.setCardImage(cardId, file);
      queryClient.setQueryData([...CARDS_KEY, "card", cardId], response);
      await queryClient.invalidateQueries({ queryKey: CARDS_KEY });
      toast.success("Picture saved.");
    } catch (e) {
      toast.error(describeError(e).message);
    } finally {
      setBusy(false);
    }
  }

  const picture =
    version === null ? (
      <div className="flex h-28 w-20 flex-col items-center justify-center gap-1 rounded-md border border-dashed text-center text-[11px] text-muted-foreground">
        <ImagePlus className="size-5" aria-hidden="true" />
        {canChange ? "Add picture" : "No picture"}
      </div>
    ) : (
      <img
        src={api.cardImageUrl(cardId, version)}
        alt={name}
        className="h-28 w-20 rounded-md border object-cover"
      />
    );

  if (!canChange) return picture;
  return (
    <label
      htmlFor={inputId}
      className={busy ? "cursor-wait opacity-60" : "cursor-pointer"}
      title="Add or change the picture"
    >
      {picture}
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        aria-label="Card picture"
        disabled={busy}
        onChange={(e) => void upload(e.target.files?.[0])}
      />
    </label>
  );
}
